import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { LithuanianTimePicker } from '@/components/ui/lithuanian-time-picker';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  User, 
  Users, 
  Heart, 
  Plus, 
  Trash2, 
  Edit2, 
  Eye, 
  Crown, 
  Lock,
  X,
  ArrowRight,
  AlertTriangle,
  MessageCircle,
  Calendar,
  Send,
  Compass
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import UserMenu from '@/components/UserMenu';
import Footer from '@/components/Footer';
import VIPModal from '@/components/VIPModal';
import axios from 'axios';
import API_BASE_URL from '@/utils/apiUrl';

const BACKEND_URL = API_BASE_URL;

// Lithuanian keyboard component
const LithuanianKeyboard = ({ onInsert }) => {
  const letters = ['ą', 'č', 'ę', 'ė', 'į', 'š', 'ų', 'ū', 'ž', 'Ą', 'Č', 'Ę', 'Ė', 'Į', 'Š', 'Ų', 'Ū', 'Ž'];
  
  return (
    <div className="flex flex-wrap gap-1 justify-center p-2 bg-secondary/30 rounded-lg border border-border/50">
      {letters.map((letter) => (
        <button
          key={letter}
          type="button"
          onClick={() => onInsert(letter)}
          className="w-7 h-7 rounded bg-amber-500/20 hover:bg-amber-500/40 text-amber-400 font-medium text-xs transition-all duration-200 border border-amber-500/30"
        >
          {letter}
        </button>
      ))}
    </div>
  );
};

// Person Card Component
const PersonCard = ({ person, category, isMe, onView, onEdit, onDelete, onMove }) => {
  const getCategoryIcon = () => {
    if (isMe) return <User className="w-5 h-5 text-amber-400" />;
    if (category === 'family') return <Heart className="w-5 h-5 text-pink-400" />;
    return <Users className="w-5 h-5 text-blue-400" />;
  };

  const getCategoryColor = () => {
    if (isMe) return 'border-amber-500/30 bg-amber-500/5';
    if (category === 'family') return 'border-pink-500/30 bg-pink-500/5';
    return 'border-blue-500/30 bg-blue-500/5';
  };

  return (
    <Card className={`p-4 ${getCategoryColor()} hover:border-primary/50 transition-colors`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-secondary/50 flex items-center justify-center">
            {getCategoryIcon()}
          </div>
          <div>
            <h4 className="font-semibold text-foreground">
              {person.firstName} {person.lastName}
            </h4>
            <p className="text-xs text-muted-foreground">
              {person.day}/{person.month}/{person.year} • Gyvenimo kelias: {person.lifePath}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onView(person)}
            className="text-primary hover:bg-primary/10"
            title="Peržiūrėti analizę"
            data-testid={`view-person-${person.id}`}
          >
            <Eye className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onEdit(person, isMe)}
            className="text-muted-foreground hover:text-foreground"
            title="Redaguoti"
          >
            <Edit2 className="w-4 h-4" />
          </Button>
          {!isMe && (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => { e.stopPropagation(); onMove(person, category); }}
                className="text-muted-foreground hover:text-foreground cursor-pointer"
                title="Perkelti kategoriją"
              >
                <ArrowRight className="w-4 h-4" />
              </Button>
              <button
                type="button"
                onClick={(e) => { 
                  e.preventDefault();
                  e.stopPropagation(); 
                  console.log('Delete clicked for:', person.firstName);
                  onDelete(person); 
                }}
                className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-red-500/20 hover:bg-red-500/40 text-red-400 hover:text-red-300 transition-colors"
                title="Pašalinti"
                data-testid={`delete-person-${person.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>
    </Card>
  );
};

// Empty Slot Component
const EmptySlot = ({ category, onClick, disabled }) => {
  const getCategoryColor = () => {
    if (category === 'family') return 'border-pink-500/20 hover:border-pink-500/40';
    return 'border-blue-500/20 hover:border-blue-500/40';
  };

  return (
    <Card 
      className={`p-4 border-dashed ${getCategoryColor()} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} transition-colors`}
      onClick={disabled ? undefined : onClick}
    >
      <div className="flex items-center justify-center gap-2 text-muted-foreground">
        {disabled ? (
          <>
            <Lock className="w-4 h-4" />
            <span className="text-sm">Slotas užimtas</span>
          </>
        ) : (
          <>
            <Plus className="w-4 h-4" />
            <span className="text-sm">Pridėti asmenį</span>
          </>
        )}
      </div>
    </Card>
  );
};

// Add Person Modal
const AddPersonModal = ({ isOpen, onClose, onSubmit, category, setCategory }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    day: '',
    month: '',
    year: '',
    gender: ''
  });
  const [activeField, setActiveField] = useState('firstName');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.lastName || !formData.day || !formData.month || !formData.year) {
      toast.error('Užpildykite visus privalomus laukus');
      return;
    }

    setLoading(true);
    await onSubmit({ ...formData, category });
    setLoading(false);
    
    // Reset form
    setFormData({ firstName: '', lastName: '', day: '', month: '', year: '', gender: '' });
  };

  const insertLetter = (letter) => {
    setFormData(prev => ({
      ...prev,
      [activeField]: prev[activeField] + letter
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="card-mystical max-w-md w-full p-6 relative max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10"
        >
          <X className="w-5 h-5 text-muted-foreground" />
        </button>

        <h2 className="font-display text-xl text-amber-400 mb-6">Pridėti į ratą</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Category Selection */}
          <div>
            <Label className="text-foreground mb-2 block">Kategorija *</Label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setCategory('family')}
                className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                  category === 'family' 
                    ? 'border-pink-500 bg-pink-500/10 text-pink-400' 
                    : 'border-border/50 text-muted-foreground hover:border-pink-500/50'
                }`}
              >
                <Heart className="w-5 h-5 mx-auto mb-1" />
                <span className="text-sm">Šeima</span>
              </button>
              <button
                type="button"
                onClick={() => setCategory('friends')}
                className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                  category === 'friends' 
                    ? 'border-blue-500 bg-blue-500/10 text-blue-400' 
                    : 'border-border/50 text-muted-foreground hover:border-blue-500/50'
                }`}
              >
                <Users className="w-5 h-5 mx-auto mb-1" />
                <span className="text-sm">Draugai</span>
              </button>
            </div>
          </div>

          {/* Name Fields */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="firstName" className="text-foreground mb-1 block text-sm">Vardas *</Label>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                onFocus={() => setActiveField('firstName')}
                placeholder="Vardas"
                className="bg-input border-border"
              />
            </div>
            <div>
              <Label htmlFor="lastName" className="text-foreground mb-1 block text-sm">Pavardė *</Label>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                onFocus={() => setActiveField('lastName')}
                placeholder="Pavardė"
                className="bg-input border-border"
              />
            </div>
          </div>

          {/* Lithuanian Keyboard */}
          <LithuanianKeyboard onInsert={insertLetter} />

          {/* Birth Date */}
          <div>
            <Label className="text-foreground mb-2 block text-sm">Gimimo data *</Label>
            <div className="grid grid-cols-3 gap-2">
              <Input
                type="number"
                min="1"
                max="31"
                value={formData.day}
                onChange={(e) => setFormData({ ...formData, day: e.target.value })}
                placeholder="DD"
                className="bg-input border-border text-center"
              />
              <Input
                type="number"
                min="1"
                max="12"
                value={formData.month}
                onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                placeholder="MM"
                className="bg-input border-border text-center"
              />
              <Input
                type="number"
                min="1900"
                max={new Date().getFullYear()}
                value={formData.year}
                onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                placeholder="YYYY"
                className="bg-input border-border text-center"
              />
            </div>
          </div>

          {/* Gender */}
          <div>
            <Label className="text-foreground mb-2 block text-sm">Lytis (nebūtina)</Label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, gender: 'female' })}
                className={`flex-1 p-2 rounded-lg border transition-colors ${
                  formData.gender === 'female' 
                    ? 'border-purple-500 bg-purple-500/10 text-purple-400' 
                    : 'border-border/50 text-muted-foreground'
                }`}
              >
                ♀ Moteris
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, gender: 'male' })}
                className={`flex-1 p-2 rounded-lg border transition-colors ${
                  formData.gender === 'male' 
                    ? 'border-blue-500 bg-blue-500/10 text-blue-400' 
                    : 'border-border/50 text-muted-foreground'
                }`}
              >
                ♂ Vyras
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-semibold"
          >
            {loading ? 'Pridedama...' : 'Pridėti'}
          </Button>
        </form>
      </Card>
    </div>
  );
};

// Edit Profile Modal
const EditProfileModal = ({ isOpen, onClose, onSubmit, person, isMe }) => {
  const [formData, setFormData] = useState({
    firstName: person?.firstName || '',
    lastName: person?.lastName || '',
    day: person?.day || '',
    month: person?.month || '',
    year: person?.year || '',
    gender: person?.gender || ''
  });
  const [activeField, setActiveField] = useState('firstName');
  const [loading, setLoading] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  useEffect(() => {
    if (person) {
      setFormData({
        firstName: person.firstName || '',
        lastName: person.lastName || '',
        day: person.day || '',
        month: person.month || '',
        year: person.year || '',
        gender: person.gender || ''
      });
    }
  }, [person]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if date changed for "me" profile
    if (isMe && (
      formData.day !== person.day ||
      formData.month !== person.month ||
      formData.year !== person.year
    )) {
      if (!showWarning) {
        setShowWarning(true);
        return;
      }
    }

    setLoading(true);
    await onSubmit(formData, person.id, isMe);
    setLoading(false);
    setShowWarning(false);
  };

  const insertLetter = (letter) => {
    setFormData(prev => ({
      ...prev,
      [activeField]: prev[activeField] + letter
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="card-mystical max-w-md w-full p-6 relative max-h-[90vh] overflow-y-auto">
        <button
          onClick={() => { onClose(); setShowWarning(false); }}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10"
        >
          <X className="w-5 h-5 text-muted-foreground" />
        </button>

        <h2 className="font-display text-xl text-amber-400 mb-6">
          {isMe ? 'Redaguoti profilį' : 'Redaguoti asmenį'}
        </h2>

        {showWarning && isMe && (
          <div className="mb-4 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-amber-400 font-semibold">Dėmesio!</p>
                <p className="text-xs text-foreground/80 mt-1">
                  Pakeitus gimimo datą, bus perskaičiuota visa jūsų asmeninė analizė.
                </p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-foreground mb-1 block text-sm">Vardas</Label>
              <Input
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                onFocus={() => setActiveField('firstName')}
                className="bg-input border-border"
              />
            </div>
            <div>
              <Label className="text-foreground mb-1 block text-sm">Pavardė</Label>
              <Input
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                onFocus={() => setActiveField('lastName')}
                className="bg-input border-border"
              />
            </div>
          </div>

          <LithuanianKeyboard onInsert={insertLetter} />

          <div>
            <Label className="text-foreground mb-2 block text-sm">Gimimo data</Label>
            <div className="grid grid-cols-3 gap-2">
              <Input
                type="number"
                value={formData.day}
                onChange={(e) => setFormData({ ...formData, day: e.target.value })}
                placeholder="DD"
                className="bg-input border-border text-center"
              />
              <Input
                type="number"
                value={formData.month}
                onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                placeholder="MM"
                className="bg-input border-border text-center"
              />
              <Input
                type="number"
                value={formData.year}
                onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                placeholder="YYYY"
                className="bg-input border-border text-center"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-semibold"
          >
            {loading ? 'Išsaugoma...' : (showWarning && isMe ? 'Patvirtinti pakeitimus' : 'Išsaugoti')}
          </Button>
        </form>
      </Card>
    </div>
  );
};

// VIP+ Question Modal
const QuestionModal = ({ isOpen, onClose, user }) => {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!message.trim()) {
      toast.error('Įveskite klausimą');
      return;
    }

    setLoading(true);
    try {
      // Send email via backend
      const response = await axios.post(`${BACKEND_URL}/api/email/vip-question`, {
        userName: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
        userEmail: user?.email,
        message: message.trim()
      });
      
      if (response.data.success) {
        toast.success('Klausimas išsiųstas!');
        setMessage('');
        onClose();
      } else {
        toast.error('Nepavyko išsiųsti');
      }
    } catch (error) {
      console.error('Question send error:', error);
      toast.error('Klaida siunčiant klausimą');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="card-mystical max-w-md w-full p-6 relative max-h-[90vh] overflow-y-auto bg-[#0f0f23] border-purple-500/30">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10"
        >
          <X className="w-5 h-5 text-muted-foreground" />
        </button>

        <div className="flex items-center gap-3 mb-6">
          <MessageCircle className="w-6 h-6 text-purple-400" />
          <h2 className="font-display text-xl text-purple-400">Užduoti klausimą</h2>
        </div>

        <div className="mb-4 p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
          <p className="text-xs text-purple-300">
            <span className="font-semibold">Siuntėjas:</span> {user?.firstName} {user?.lastName}
          </p>
          <p className="text-xs text-purple-300/70">{user?.email}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="question" className="text-foreground mb-2 block text-sm">
              Jūsų klausimas *
            </Label>
            <Textarea
              id="question"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Rašykite savo klausimą čia..."
              className="bg-input border-border min-h-[150px]"
              rows={6}
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-semibold"
          >
            {loading ? (
              'Siunčiama...'
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Siųsti klausimą
              </>
            )}
          </Button>
        </form>
      </Card>
    </div>
  );
};

// VIP+ Consultation Modal
const ConsultationModal = ({ isOpen, onClose, user }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    birthDay: '',
    birthMonth: '',
    birthYear: '',
    birthPlace: '',
    birthTime: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.fullName.trim() || !formData.birthDay || !formData.birthMonth || !formData.birthYear || !formData.birthPlace.trim()) {
      toast.error('Užpildykite visus privalomus laukus');
      return;
    }

    setLoading(true);
    try {
      const birthDate = `${formData.birthYear}-${String(formData.birthMonth).padStart(2, '0')}-${String(formData.birthDay).padStart(2, '0')}`;
      
      // Send email via backend
      const response = await axios.post(`${BACKEND_URL}/api/email/vip-consultation`, {
        userName: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
        userEmail: user?.email,
        fullName: formData.fullName.trim(),
        birthDate: birthDate,
        birthPlace: formData.birthPlace.trim(),
        birthTime: formData.birthTime || 'Nežinomas',
        message: formData.message.trim()
      });
      
      if (response.data.success) {
        toast.success('Konsultacijos užklausa išsiųsta!');
        setFormData({ fullName: '', birthDay: '', birthMonth: '', birthYear: '', birthPlace: '', birthTime: '', message: '' });
        onClose();
      } else {
        toast.error('Nepavyko išsiųsti');
      }
    } catch (error) {
      console.error('Consultation send error:', error);
      toast.error('Klaida siunčiant užklausą');
    } finally {
      setLoading(false);
    }
  };

  // Generate day options
  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  
  // Lithuanian months
  const months = [
    { value: 1, label: 'Sausis' },
    { value: 2, label: 'Vasaris' },
    { value: 3, label: 'Kovas' },
    { value: 4, label: 'Balandis' },
    { value: 5, label: 'Gegužė' },
    { value: 6, label: 'Birželis' },
    { value: 7, label: 'Liepa' },
    { value: 8, label: 'Rugpjūtis' },
    { value: 9, label: 'Rugsėjis' },
    { value: 10, label: 'Spalis' },
    { value: 11, label: 'Lapkritis' },
    { value: 12, label: 'Gruodis' }
  ];
  
  // Generate year options (100 years back)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="card-mystical max-w-md w-full p-6 relative max-h-[90vh] overflow-y-auto bg-[#0f0f23] border-amber-500/30">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10"
        >
          <X className="w-5 h-5 text-muted-foreground" />
        </button>

        <div className="flex items-center gap-3 mb-6">
          <Calendar className="w-6 h-6 text-amber-400" />
          <h2 className="font-display text-xl text-amber-400">Užsakyti konsultaciją</h2>
        </div>

        <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
          <p className="text-xs text-amber-300">
            <span className="font-semibold">Siuntėjas:</span> {user?.firstName} {user?.lastName}
          </p>
          <p className="text-xs text-amber-300/70">{user?.email}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="fullName" className="text-foreground mb-1 block text-sm">
              Vardas ir pavardė (kaip dokumente) *
            </Label>
            <Input
              id="fullName"
              value={formData.fullName}
              onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
              placeholder="Pilnas vardas ir pavardė"
              className="bg-input border-border"
            />
          </div>

          <div>
            <Label className="text-foreground mb-2 block text-sm">
              Gimimo data *
            </Label>
            <div className="grid grid-cols-3 gap-2">
              <select
                value={formData.birthDay}
                onChange={(e) => setFormData({ ...formData, birthDay: e.target.value })}
                className="h-10 px-3 py-2 bg-input border border-border rounded-md text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Diena</option>
                {days.map(day => (
                  <option key={day} value={day}>{day}</option>
                ))}
              </select>
              
              <select
                value={formData.birthMonth}
                onChange={(e) => setFormData({ ...formData, birthMonth: e.target.value })}
                className="h-10 px-3 py-2 bg-input border border-border rounded-md text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Mėnuo</option>
                {months.map(month => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>
              
              <select
                value={formData.birthYear}
                onChange={(e) => setFormData({ ...formData, birthYear: e.target.value })}
                className="h-10 px-3 py-2 bg-input border border-border rounded-md text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Metai</option>
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <Label htmlFor="birthPlace" className="text-foreground mb-1 block text-sm">
              Gimimo vieta (šalis/miestas/kaimas) *
            </Label>
            <Input
              id="birthPlace"
              value={formData.birthPlace}
              onChange={(e) => setFormData({ ...formData, birthPlace: e.target.value })}
              placeholder="pvz., Lietuva, Vilnius"
              className="bg-input border-border"
            />
          </div>

          <div>
            <Label htmlFor="birthTime" className="text-foreground mb-1 block text-sm">
              Gimimo laikas (jei žinote)
            </Label>
            <LithuanianTimePicker
              value={formData.birthTime}
              onChange={(time) => setFormData({ ...formData, birthTime: time })}
              placeholder="Pasirinkti laiką"
            />
          </div>

          <div>
            <Label htmlFor="consultMessage" className="text-foreground mb-1 block text-sm">
              Papildoma informacija
            </Label>
            <Textarea
              id="consultMessage"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="Ką norėtumėte sužinoti konsultacijos metu?"
              className="bg-input border-border min-h-[100px]"
              rows={4}
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-semibold"
          >
            {loading ? (
              'Siunčiama...'
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Siųsti užklausą
              </>
            )}
          </Button>
        </form>
      </Card>
    </div>
  );
};

// Main Page Component
export default function MyCirclePage() {
  const navigate = useNavigate();
  const { user, token, isAuthenticated, loading: authLoading } = useAuth();
  const [circleData, setCircleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showVIPModal, setShowVIPModal] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [isEditingMe, setIsEditingMe] = useState(false);
  const [addCategory, setAddCategory] = useState('family');
  // Custom groups for Annual VIPs
  const [customGroups, setCustomGroups] = useState([]);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  // VIP+ Modals
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [showConsultationModal, setShowConsultationModal] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      toast.error('Prašome prisijungti');
      navigate('/');
      return;
    }
    
    if (isAuthenticated && token) {
      fetchCircleData();
    }
  }, [isAuthenticated, authLoading, token, navigate]);

  const fetchCircleData = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/users/my-circle`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setCircleData(response.data);
        
        // Extract custom groups from myCircle data (any category that's not 'family' or 'friends')
        const myCircle = response.data.myCircle || {};
        const standardCategories = ['me', 'family', 'friends'];
        const customCats = Object.keys(myCircle).filter(cat => !standardCategories.includes(cat));
        setCustomGroups(customCats);
      }
    } catch (error) {
      console.error('Failed to fetch circle:', error);
      toast.error('Nepavyko gauti duomenų');
    } finally {
      setLoading(false);
    }
  };

  // Handler for creating a new custom group
  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      toast.error('Įveskite grupės pavadinimą');
      return;
    }
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/users/my-circle/groups`,
        { groupName: newGroupName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setCustomGroups(response.data.customGroups || []);
        setNewGroupName('');
        setShowCreateGroupModal(false);
        toast.success(response.data.message);
        fetchCircleData(); // Refresh data
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Klaida kuriant grupę');
    }
  };

  const handleAddPerson = async (personData) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/users/my-circle/add`,
        personData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Check for successful response
      if (response.data && response.data.success) {
        toast.success(response.data.message || 'Asmuo pridėtas!');
        setShowAddModal(false);
        await fetchCircleData();
        return; // Exit early on success
      }
      
      // Check if limit was reached
      if (response.data && response.data.limitReached) {
        setShowAddModal(false);
        setShowVIPModal(true);
        return;
      }
      
      // Handle explicit failure from backend
      if (response.data && !response.data.success) {
        toast.error(response.data.message || 'Klaida pridedant asmenį');
        return;
      }
      
    } catch (error) {
      console.error('Add person error:', error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Klaida pridedant asmenį';
      toast.error(errorMessage);
    }
  };

  const handleEditPerson = async (formData, personId, isMe) => {
    try {
      const endpoint = isMe 
        ? `${BACKEND_URL}/api/users/my-circle/me`
        : `${BACKEND_URL}/api/users/my-circle/${personId}`;
      
      const response = await axios.put(endpoint, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        setShowEditModal(false);
        setSelectedPerson(null);
        fetchCircleData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Klaida atnaujinant duomenis');
    }
  };

  const handleDeletePerson = async (person) => {
    const confirmed = window.confirm(`Ar tikrai norite pašalinti ${person.firstName}? Pastaba: skaičiavimų limitas nepasikeis.`);
    
    if (!confirmed) {
      return;
    }

    try {
      const response = await axios.delete(
        `${BACKEND_URL}/api/users/my-circle/${person.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchCircleData();
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Klaida šalinant asmenį');
    }
  };

  const handleDeleteGroup = async (groupKey, memberCount = 0) => {
    let confirmMessage;
    if (memberCount > 0) {
      confirmMessage = `Ar tikrai norite pašalinti grupę "${groupKey}"?\n\n⚠️ DĖMESIO: Grupėje yra ${memberCount} ${memberCount === 1 ? 'asmuo' : 'asmenys'}. Visi jie bus pašalinti kartu su grupe.`;
    } else {
      confirmMessage = `Ar tikrai norite pašalinti tuščią grupę "${groupKey}"?`;
    }
    
    const confirmed = window.confirm(confirmMessage);
    
    if (!confirmed) {
      return;
    }

    try {
      const response = await axios.delete(
        `${BACKEND_URL}/api/users/my-circle/groups/${groupKey}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
        setCustomGroups(prev => prev.filter(g => g !== groupKey));
        fetchCircleData();
      }
    } catch (error) {
      console.error('Delete group error:', error);
      toast.error(error.response?.data?.detail || 'Klaida šalinant grupę');
    }
  };

  const handleMovePerson = async (person, currentCategory) => {
    const newCategory = currentCategory === 'family' ? 'friends' : 'family';
    
    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/users/my-circle/${person.id}`,
        { category: newCategory },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(`${person.firstName} perkeltas(-a) į ${newCategory === 'family' ? 'šeimą' : 'draugus'}`);
        fetchCircleData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Klaida perkeliant asmenį');
    }
  };

  const handleViewPerson = (person) => {
    // Store person data in localStorage and navigate to results
    const dataToStore = {
      firstName: person.firstName,
      lastName: person.lastName,
      day: person.day,
      month: person.month,
      year: person.year,
      lifePath: person.lifePath,
      gender: person.gender
    };
    localStorage.setItem('numerologyData', JSON.stringify(dataToStore));
    navigate('/rezultatai');
  };

  const handleOpenEdit = (person, isMe) => {
    setSelectedPerson(person);
    setIsEditingMe(isMe);
    setShowEditModal(true);
  };

  const handleOpenAdd = (category) => {
    setAddCategory(category);
    setShowAddModal(true);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const myCircle = circleData?.myCircle || {};
  const limits = circleData?.limits || {};
  const vipStatus = circleData?.vipStatus || {};

  const familySlots = 4;
  const friendsSlots = 2;
  const familyList = myCircle.family || [];
  const friendsList = myCircle.friends || [];

  return (
    <div className="min-h-screen pb-16">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-foreground/70 hover:text-primary"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Grįžti
            </Button>
            <UserMenu />
          </div>
          
          <div className="text-center">
            <div className="inline-flex items-center gap-3 mb-2">
              <Compass className="w-8 h-8 text-amber-400" />
              <h1 className="font-display text-3xl text-primary">Mano ratas</h1>
            </div>
            <p className="text-muted-foreground">
              Sekite savo artimųjų numerologines analizes
            </p>
          </div>
        </div>

        {/* Limits Info */}
        <Card className="card-mystical p-4 mb-6 border-primary/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {vipStatus.isVIP ? (
                <Crown className="w-5 h-5 text-amber-400" />
              ) : (
                <User className="w-5 h-5 text-muted-foreground" />
              )}
              <div>
                <p className="text-sm text-foreground">
                  {vipStatus.isVIP 
                    ? `VIP ${vipStatus.isAnnualVIP ? (vipStatus.plan?.includes('premium') ? '(Metinis + Konsultacijos)' : '(Metinis)') : '(Mėnesinis)'}`
                    : 'Nemokama versija'
                  }
                </p>
                <p className="text-xs text-muted-foreground">
                  {limits.isUnlimited 
                    ? 'Neriboti skaičiavimai'
                    : `Panaudota: ${limits.used} / ${limits.total}`
                  }
                </p>
              </div>
            </div>
            
            {!vipStatus.isVIP && (
              <Button
                size="sm"
                onClick={() => setShowVIPModal(true)}
                className="bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold"
              >
                <Crown className="w-4 h-4 mr-1" />
                Atrakinti VIP
              </Button>
            )}
          </div>
        </Card>

        {/* VIP+ Premium Section - For all VIP users */}
        {vipStatus.isVIP && (
          <Card className="card-mystical p-4 mb-6 border-purple-500/30 bg-gradient-to-br from-purple-500/5 to-amber-500/5">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-full bg-gradient-to-br from-purple-500/20 to-amber-500/20">
                <Crown className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h3 className="font-display text-lg text-amber-400">VIP+ Privilegijos</h3>
                <p className="text-xs text-muted-foreground">Jūsų asmeninis numerologijos asistentas</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button
                onClick={() => setShowConsultationModal(true)}
                className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-semibold h-auto py-3 shadow-none"
                style={{ boxShadow: 'none' }}
                data-testid="consultation-button"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Užsakyti konsultaciją
              </Button>
              
              <Button
                onClick={() => setShowQuestionModal(true)}
                variant="outline"
                className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10 h-auto py-3 shadow-none"
                style={{ boxShadow: 'none' }}
                data-testid="question-button"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Užduoti klausimą
              </Button>
            </div>
          </Card>
        )}

        {/* Aš (Me) Section */}
        <div className="mb-6">
          <h2 className="font-display text-lg text-amber-400 mb-3 flex items-center gap-2">
            <User className="w-5 h-5" />
            Aš
          </h2>
          {myCircle.me && (
            <PersonCard
              person={myCircle.me}
              category="me"
              isMe={true}
              onView={handleViewPerson}
              onEdit={handleOpenEdit}
              onDelete={() => {}}
              onMove={() => {}}
            />
          )}
        </div>

        {/* Šeima (Family) Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-lg text-pink-400 flex items-center gap-2">
              <Heart className="w-5 h-5" />
              Šeima
            </h2>
            {!limits.isUnlimited && (
              <span className="text-xs text-muted-foreground">
                {familyList.length} / {familySlots}
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {familyList.map((person) => (
              <PersonCard
                key={person.id}
                person={person}
                category="family"
                isMe={false}
                onView={handleViewPerson}
                onEdit={handleOpenEdit}
                onDelete={handleDeletePerson}
                onMove={handleMovePerson}
              />
            ))}
            {/* Add button - dynamic instead of empty slots */}
            {(limits.isUnlimited || (familyList.length < 4 && limits.used < limits.total)) && (
              <button
                onClick={() => handleOpenAdd('family')}
                className="p-4 rounded-lg border-2 border-dashed border-pink-500/30 hover:border-pink-500/50 hover:bg-pink-500/5 transition-colors flex items-center justify-center gap-2 text-pink-400"
              >
                <Plus className="w-5 h-5" />
                <span>Pridėti į šeimą</span>
              </button>
            )}
          </div>
        </div>

        {/* Draugai (Friends) Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-lg text-blue-400 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Draugai
            </h2>
            {!limits.isUnlimited && (
              <span className="text-xs text-muted-foreground">
                {friendsList.length} / {friendsSlots}
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {friendsList.map((person) => (
              <PersonCard
                key={person.id}
                person={person}
                category="friends"
                isMe={false}
                onView={handleViewPerson}
                onEdit={handleOpenEdit}
                onDelete={handleDeletePerson}
                onMove={handleMovePerson}
              />
            ))}
            {/* Add button - Annual VIP has no limit */}
            {(limits.isUnlimited || (friendsList.length < 2 && limits.used < limits.total)) && (
              <button
                onClick={() => handleOpenAdd('friends')}
                className="p-4 rounded-lg border-2 border-dashed border-blue-500/30 hover:border-blue-500/50 hover:bg-blue-500/5 transition-colors flex items-center justify-center gap-2 text-blue-400"
              >
                <Plus className="w-5 h-5" />
                <span>Pridėti draugą</span>
              </button>
            )}
          </div>
        </div>

        {/* Custom Groups (Annual VIP only) */}
        {limits.isUnlimited && customGroups.map((groupKey) => {
          const groupMembers = myCircle[groupKey] || [];
          const groupDisplayName = groupKey.charAt(0).toUpperCase() + groupKey.slice(1).replace(/_/g, ' ');
          
          return (
            <div key={groupKey} className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display text-lg text-purple-400 flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  {groupDisplayName}
                  {groupMembers.length > 0 && (
                    <span className="text-xs text-muted-foreground font-normal">({groupMembers.length})</span>
                  )}
                </h2>
                {/* Delete Group Button - always visible for custom groups */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDeleteGroup(groupKey, groupMembers.length);
                  }}
                  className="inline-flex items-center justify-center h-8 px-3 rounded-md text-red-400 hover:bg-red-500/20 hover:text-red-300 transition-colors text-xs"
                  title={groupMembers.length > 0 ? `Pašalinti grupę (${groupMembers.length} nariai)` : 'Pašalinti tuščią grupę'}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  <span>Pašalinti</span>
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {groupMembers.map((person) => (
                  <PersonCard
                    key={person.id}
                    person={person}
                    category={groupKey}
                    isMe={false}
                    onView={handleViewPerson}
                    onEdit={handleOpenEdit}
                    onDelete={handleDeletePerson}
                    onMove={handleMovePerson}
                  />
                ))}
                <button
                  onClick={() => handleOpenAdd(groupKey)}
                  className="p-4 rounded-lg border-2 border-dashed border-purple-500/30 hover:border-purple-500/50 hover:bg-purple-500/5 transition-colors flex items-center justify-center gap-2 text-purple-400"
                >
                  <Plus className="w-5 h-5" />
                  <span>Pridėti į {groupDisplayName.toLowerCase()}</span>
                </button>
              </div>
            </div>
          );
        })}

        {/* Create New Group Button (Annual VIP only) */}
        {limits.isUnlimited && (
          <div className="mb-8">
            <button
              onClick={() => setShowCreateGroupModal(true)}
              className="w-full p-4 rounded-lg border-2 border-dashed border-amber-500/30 hover:border-amber-500/50 hover:bg-amber-500/5 transition-colors flex items-center justify-center gap-2 text-amber-400"
            >
              <Plus className="w-5 h-5" />
              <span>Sukurti naują grupę</span>
            </button>
          </div>
        )}

        {/* Info Note */}
        {!limits.isUnlimited && (
          <Card className="p-4 bg-amber-500/5 border-amber-500/20">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-foreground/80">
                <p className="font-semibold text-amber-400 mb-1">Svarbu žinoti</p>
                <p>
                  Pašalinus asmenį iš rato, skaičiavimų limitas nepasikeis. 
                  Kiekvienas unikalus vardas skaičiuojamas vieną kartą visam laikui.
                </p>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Modals */}
      <AddPersonModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddPerson}
        category={addCategory}
        setCategory={setAddCategory}
      />

      <EditProfileModal
        isOpen={showEditModal}
        onClose={() => { setShowEditModal(false); setSelectedPerson(null); }}
        onSubmit={handleEditPerson}
        person={selectedPerson}
        isMe={isEditingMe}
      />

      <VIPModal
        isOpen={showVIPModal}
        onClose={() => setShowVIPModal(false)}
        onSuccess={() => { setShowVIPModal(false); fetchCircleData(); }}
        userData={user}
      />

      {/* Create Group Modal (Annual VIP only) */}
      {showCreateGroupModal && (
        <div className="fixed inset-0 flex items-center justify-center p-4 z-50" style={{ backgroundColor: 'rgba(15, 15, 35, 0.95)' }}>
          <Card className="max-w-md w-full bg-background border-border p-6 relative">
            <button
              onClick={() => setShowCreateGroupModal(false)}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
            >
              <X className="w-5 h-5" />
            </button>
            
            <h3 className="font-display text-xl text-amber-400 mb-4">Sukurti naują grupę</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Kaip VIP narys, galite kurti neribotą skaičių grupių (pvz., "Kolegos", "Giminaičiai", "Partneriai").
            </p>
            
            <div className="mb-4">
              <Label className="text-foreground mb-2 block text-sm">Grupės pavadinimas</Label>
              <Input
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="pvz., Kolegos"
                className="bg-input border-border"
                onKeyDown={(e) => e.key === 'Enter' && handleCreateGroup()}
              />
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowCreateGroupModal(false)}
                className="flex-1"
              >
                Atšaukti
              </Button>
              <Button
                onClick={handleCreateGroup}
                className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-semibold"
              >
                Sukurti
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* VIP+ Question Modal */}
      <QuestionModal
        isOpen={showQuestionModal}
        onClose={() => setShowQuestionModal(false)}
        user={user}
      />

      {/* VIP+ Consultation Modal */}
      <ConsultationModal
        isOpen={showConsultationModal}
        onClose={() => setShowConsultationModal(false)}
        user={user}
      />

      {/* Footer */}
      <Footer />
    </div>
  );
}
